import React from "react";
import { notify } from "@/shared/utils/notify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  promotionRulesService,
  promotionRuleCategoriesService,
  promotionRuleExcludedProductsService,
  promotionRuleFreeProductsService,
} from "../../../shared/services";

export interface ExcludedProduct {
  id: number;
  name: string;
}

export interface FreeSlotProduct {
  id: number;
  name: string;
}

export interface SelectedDaysType {
  monday: "true" | "false";
  tuesday: "true" | "false";
  wednesday: "true" | "false";
  thursday: "true" | "false";
  friday: "true" | "false";
  saturday: "true" | "false";
  sunday: "true" | "false";
}

export interface CategoryConfig {
  category_id: number;
  role: "primary" | "secondary";
  required_quantity: number;
  is_required: boolean;
}

export interface CreatePromotionData {
  name: string;
  description?: string;
  promo_type: "2x1" | "3x2" | "custom";
  discount_type?: "cheapest_free" | "percentage" | "fixed_price";
  discount_value?: number;
}

const schema = yup.object().shape({
  name: yup.string().trim().required("El nombre de la promoción es obligatorio"),
  description: yup.string().optional(),
  discount_type: yup
    .string()
    .oneOf(["cheapest_free", "percentage", "fixed_price"])
    .optional(),
  discount_value: yup.number().optional(),
});

export const useCreatePromotion = ({
  setOpenModal,
  days,
  categories,
  excludedProducts,
  freeSlotProducts,
  promoType,
  onSuccess,
  editingId,
  defaultValues,
}: {
  setOpenModal: (val: boolean) => void;
  days: SelectedDaysType;
  categories: CategoryConfig[];
  excludedProducts: ExcludedProduct[];
  freeSlotProducts: FreeSlotProduct[];
  promoType: "2x1" | "3x2" | "custom";
  onSuccess?: () => void;
  editingId?: number | null;
  defaultValues?: Partial<CreatePromotionData>;
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreatePromotionData>({
    resolver: yupResolver(schema),
    defaultValues: {
      discount_type: "cheapest_free",
      ...defaultValues,
    },
  });

  React.useEffect(() => {
    if (defaultValues) {
      reset({ discount_type: "cheapest_free", ...defaultValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues?.name, defaultValues?.description, editingId]);

  const onSubmit = async (data: CreatePromotionData) => {
    const validCategories = categories.filter((c) => c.category_id > 0);
    const hasPrimary = validCategories.some((c) => c.role === "primary");

    if (!hasPrimary) {
      notify.error("Debes agregar al menos una categoría principal");
      return;
    }

    const categoryIds = validCategories.map((c) => c.category_id);
    const duplicateCategoryIds = categoryIds.filter(
      (id, index) => categoryIds.indexOf(id) !== index,
    );
    if (duplicateCategoryIds.length > 0) {
      const uniqueDupes = Array.from(new Set(duplicateCategoryIds));
      notify.error(
        `No puedes usar la misma categoría más de una vez (IDs: ${uniqueDupes.join(", ")})`,
      );
      return;
    }

    const hasDay = Object.values(days).some((d) => d === "true");
    if (!hasDay) {
      notify.error("Debes seleccionar al menos un día de la semana");
      return;
    }

    const excludedIds = excludedProducts.map((p) => p.id);
    if (excludedIds.some((id, i) => excludedIds.indexOf(id) !== i)) {
      notify.error("Hay productos excluidos duplicados en la lista");
      return;
    }

    const freeIds = freeSlotProducts.map((p) => p.id);
    if (freeIds.some((id, i) => freeIds.indexOf(id) !== i)) {
      notify.error("Hay productos del cupo gratis duplicados en la lista");
      return;
    }

    setIsSubmitting(true);
    try {
      let promotionId: number;

      if (editingId) {
        const updated = await promotionRulesService.patch(editingId, {
          name: data.name,
          description: data.description || null,
          promo_type: promoType,
          discount_type: data.discount_type || "cheapest_free",
          discount_value: data.discount_value ?? null,
          ...days,
        });
        promotionId = (updated as any).id ?? editingId;

        // Eliminar categorías y exclusiones existentes para reemplazar
        const existingCategories = await promotionRuleCategoriesService.find({
          query: { promotion_rule_id: editingId, $limit: 100 },
        });
        const catRows = (existingCategories as any).data ?? existingCategories;
        for (const row of catRows) {
          await promotionRuleCategoriesService.remove(row.id);
        }

        const existingExcluded =
          await promotionRuleExcludedProductsService.find({
            query: { promotion_rule_id: editingId, $limit: 100 },
          });
        const excRows = (existingExcluded as any).data ?? existingExcluded;
        for (const row of excRows) {
          await promotionRuleExcludedProductsService.remove(row.id);
        }

        const existingFree = await promotionRuleFreeProductsService.find({
          query: { promotion_rule_id: editingId, $limit: 200 },
        });
        const freeRows = (existingFree as any).data ?? existingFree;
        for (const row of freeRows) {
          await promotionRuleFreeProductsService.remove(row.id);
        }
      } else {
        const promotion = await promotionRulesService.create({
          name: data.name,
          description: data.description || null,
          promo_type: promoType,
          discount_type: data.discount_type || "cheapest_free",
          discount_value: data.discount_value ?? null,
          ...days,
          status: "inactive",
        });
        promotionId = promotion.id;
      }

      for (let i = 0; i < validCategories.length; i++) {
        const cat = validCategories[i];
        await promotionRuleCategoriesService.create({
          promotion_rule_id: promotionId,
          category_id: cat.category_id,
          role: cat.role,
          required_quantity: cat.required_quantity,
          is_required: cat.is_required ? "true" : "false",
          position: i,
        });
      }

      for (const product of excludedProducts) {
        await promotionRuleExcludedProductsService.create({
          promotion_rule_id: promotionId,
          product_id: product.id,
          note: "No aplica promoción - tarifa plena",
        });
      }

      for (const product of freeSlotProducts) {
        await promotionRuleFreeProductsService.create({
          promotion_rule_id: promotionId,
          product_id: product.id,
        });
      }

      notify.success(
        editingId
          ? "Promoción actualizada exitosamente"
          : "Promoción creada exitosamente",
      );
      reset();
      setOpenModal(false);
      onSuccess?.();
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        (editingId
          ? "Error al actualizar la promoción"
          : "Error al crear la promoción");
      notify.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onInvalid = (formErrors: Record<string, { message?: string }>) => {
    const firstError = Object.values(formErrors).find((e) => e?.message);
    if (firstError?.message) {
      notify.error(firstError.message);
      return;
    }
    notify.error("Completa los campos obligatorios (nombre y configuración)");
  };

  return {
    control,
    handleSubmit: handleSubmit(onSubmit, onInvalid),
    errors,
    reset,
    isSubmitting,
  };
};
